import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Button } from "reactstrap";
import { normalizeMetadataFiltersArray } from "helpers/metadataFilterHelpers";

const FILTER_TOOLBAR_DROPPABLE_ID = "toolbar-filters";
const FILTER_ADVANCED_DROPPABLE_ID = "advanced-filters";

const FilterPreferenceList = ({
  droppableId,
  title,
  helperText,
  fieldOrder = [],
  definitionByField = {},
}) => (
  <div className="filter-toolbar-prefs__column">
    <div className="d-flex justify-content-between align-items-start mb-2">
      <div>
        <h6 className="mb-1">{title}</h6>
        <p className="text-muted small mb-0">{helperText}</p>
      </div>
    </div>
    <Droppable droppableId={droppableId}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`filter-toolbar-prefs__dropzone${
            snapshot.isDraggingOver ? " filter-toolbar-prefs__dropzone--active" : ""
          }`}
        >
          {fieldOrder.length ? (
            fieldOrder.map((field, index) => {
              const definition = definitionByField[field];
              if (!definition) return null;
              return (
                <Draggable key={field} draggableId={field} index={index}>
                  {(dragProvided, dragSnapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      className={`filter-toolbar-prefs__item${
                        dragSnapshot.isDragging ? " filter-toolbar-prefs__item--dragging" : ""
                      }`}
                    >
                      <span
                        className="filter-toolbar-prefs__handle text-muted"
                        aria-hidden="true"
                      >
                        ⋮⋮
                      </span>
                      <span className="filter-toolbar-prefs__label">
                        {definition?.title || field}
                      </span>
                    </div>
                  )}
                </Draggable>
              );
            })
          ) : (
            <div className="filter-toolbar-prefs__empty text-muted small">
              Drag filters here
            </div>
          )}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  </div>
);

FilterPreferenceList.propTypes = {
  droppableId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  helperText: PropTypes.string,
  fieldOrder: PropTypes.arrayOf(PropTypes.string),
  definitionByField: PropTypes.object,
};

const ManageFilterToolbarPanel = ({
  metadataFilterDefinitions = [],
  toolbarFieldOrder = [],
  advancedFieldOrder = [],
  onDragEnd,
  onReset,
}) => {
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);

  const definitionByField = useMemo(() => {
    const map = {};
    normalizeMetadataFiltersArray(metadataFilterDefinitions).forEach((row) => {
      if (row?.field) map[row.field] = row;
    });
    return map;
  }, [metadataFilterDefinitions]);

  if (!metadataFilterDefinitions?.length) return null;

  if (!isCustomizeOpen) {
    return (
      <div className="filter-toolbar-prefs filter-toolbar-prefs--prompt mb-4">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <p className="mb-0 filter-toolbar-prefs__prompt-text">
            Do you want to customize filter?
          </p>
          <Button
            color="link"
            type="button"
            className="filter-toolbar-prefs__click-here p-0"
            onClick={() => setIsCustomizeOpen(true)}
          >
            Click Here
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="filter-toolbar-prefs mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h6 className="mb-1">Customize upper filters</h6>
          <p className="text-muted small mb-0">
            Drag filters between the upper toolbar and advanced filters. Changes save automatically.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button color="primary" size="md" className="border" type="button" onClick={onReset}>
            Reset to defaults
          </Button>
          <Button
            color="info"
            size="md"
            className="border"
            type="button"
            onClick={() => setIsCustomizeOpen(false)}
          >
            Hide
          </Button>
        </div>
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="row g-3">
          <div className="col-md-6">
            <FilterPreferenceList
              droppableId={FILTER_TOOLBAR_DROPPABLE_ID}
              title="Upper toolbar"
              helperText="Shown above the table"
              fieldOrder={toolbarFieldOrder}
              definitionByField={definitionByField}
            />
          </div>
          <div className="col-md-6">
            <FilterPreferenceList
              droppableId={FILTER_ADVANCED_DROPPABLE_ID}
              title="Advanced filters"
              helperText="Shown in this panel only"
              fieldOrder={advancedFieldOrder}
              definitionByField={definitionByField}
            />
          </div>
        </div>
      </DragDropContext>
    </div>
  );
};

ManageFilterToolbarPanel.propTypes = {
  metadataFilterDefinitions: PropTypes.array,
  toolbarFieldOrder: PropTypes.arrayOf(PropTypes.string),
  advancedFieldOrder: PropTypes.arrayOf(PropTypes.string),
  onDragEnd: PropTypes.func,
  onReset: PropTypes.func,
};

export default ManageFilterToolbarPanel;
